import {
  ChangeEvent,
  createContext,
  FC,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Autocomplete,
  autocompleteClasses,
  AutocompleteRenderInputParams,
  Box,
  ListSubheader,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { Controller, get, useFormContext, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { ListChildComponentProps, VariableSizeList } from "react-window";
import { styled } from "@mui/material/styles";

import { IAutocompleteOption, IFilterOptions } from "@framework/types";
import { ProgressOverlay } from "@framework/mui-page-layout";
import { useAsu } from "@framework/provider-asu";
import { useApiCall, useDeepCompareEffect } from "@framework/react-hooks";

export interface IFilterAutocompleteInput {
  name: keyof IFilterOptions;
  label?: string | number | ReactElement;
  placeholder?: string;
  controller: string;
  disabled?: boolean;
  readOnly?: boolean;
  disableClear?: boolean;
  multiple?: boolean;
  autoselect?: boolean;
  getTitle?: (item: any) => string;
  variant?: "standard" | "filled" | "outlined";
  onChange?: (
    event: ChangeEvent<unknown>,
    options: IAutocompleteOption[] | IAutocompleteOption | null,
    reason: string,
  ) => void;
  systemOptions?: IAutocompleteOption[];
  isGrouped?: boolean;
  groupName?: string | ((option: IAutocompleteOption) => string);
  filterOption?: (option: IAutocompleteOption) => boolean;
}

export const FilterAutocompleteInput: FC<IFilterAutocompleteInput> = props => {
  const {
    name,
    controller,
    variant = "standard",
    onChange,
    label,
    placeholder,
    disabled,
    readOnly,
    disableClear,
    systemOptions = [],
    isGrouped = false,
    groupName,
    filterOption,
  } = props;
  const { filterOptions: options, updateFilterOptions: setOptions } = useAsu();
  const suffix = name.split(".").pop() as string;

  const [open, setOpen] = useState(false);

  const form = useFormContext<any>();
  const error = get(form.formState.errors, name);
  const value = useWatch({ name });

  const { formatMessage } = useIntl();
  const localizedLabel = label ?? formatMessage({ id: `form.labels.${suffix}` });
  const localizedPlaceholder = placeholder ?? formatMessage({ id: `form.placeholders.${suffix}` });
  const localizedHelperText = error ? formatMessage({ id: error.message }, { label: localizedLabel }) : "";

  const abortController = new AbortController();

  const OuterElementContext = createContext({});

  const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
  });

  function useResetCache(data: any) {
    const ref = useRef<VariableSizeList>(null);
    useEffect(() => {
      if (ref.current != null) {
        ref.current.resetAfterIndex(0, true);
      }
    }, [data]);
    return ref;
  }

  const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
      boxSizing: "border-box",
      padding: 0,
      "& ul": {
        padding: 0,
        margin: 0,
      },
    },
  });

  const { fn: fetchOptions, isLoading } = useApiCall(
    (api): Promise<void> => {
      return api
        .fetchJson({
          url: `/${controller}/autocomplete`,
          signal: abortController.signal,
        })
        .then((json: Array<IAutocompleteOption>) => {
          let customOptions = json;

          if (isGrouped) {
            customOptions = json
              .filter(option => (filterOption ? filterOption(option) : true))
              .sort((a, b) => a.title.localeCompare(b.title, "ru", { numeric: true }))
              .map(
                option =>
                  ({
                    ...option,
                    group: groupName ? (typeof groupName === "string" ? groupName : groupName(option)) : "CUSTOM",
                  }) as IAutocompleteOption,
              )
              .sort((a, b) => a.group!.localeCompare(b.group!));
          }

          setOptions(name, [...systemOptions, ...customOptions]);
        })
        .catch(e => {
          const isAbort =
            e.name === "AbortError" ||
            (typeof DOMException !== "undefined" && e instanceof DOMException && e.name === "AbortError") ||
            e.code === 20;

          if (!isAbort) {
            console.error(e);
            enqueueSnackbar(formatMessage({ id: "snackbar.error" }), { variant: "error" });
          }
        });
    },
    { success: false },
  );

  useDeepCompareEffect(() => {
    if (!options[name]) {
      void fetchOptions();
    }

    return () => abortController.abort();
  }, [options]);

  function renderRow(props: ListChildComponentProps) {
    const { data, index, style } = props;
    const dataSet = data[index];

    if (isGrouped && Object.prototype.hasOwnProperty.call(dataSet, "group")) {
      return !dataSet.group ? null : (
        <ListSubheader key={dataSet.key} style={style} sx={{ p: 0 }}>
          <Box
            sx={theme => ({
              backgroundColor: theme.palette.secondary.light,
              color: theme.palette.secondary.contrastText,
              px: 2,
            })}
          >
            <FormattedMessage id={`enums.${name as string}.${dataSet.group}`} />
          </Box>
        </ListSubheader>
      );
    }

    if (!dataSet[1]?.title) {
      return null;
    }

    return (
      <Typography component="li" {...dataSet[0]} style={style}>
        {dataSet[1]?.title}
      </Typography>
    );
  }

  const VirtualizedListBox: FC = forwardRef<HTMLDivElement, HTMLAttributes<HTMLElement>>(
    function ListboxComponent(props, ref) {
      const { children, ...other } = props;
      const itemData: ReactElement[] = [];
      (children as ReactElement[]).forEach((item: ReactElement & { children?: ReactElement[] }) => {
        if (isGrouped) {
          itemData.push(item);
        }
        if (item.children) {
          itemData.push(...(item.children || []));
        }
      });

      const itemCount = itemData.length;
      const itemSize = 48;

      const getChildSize = (_child: ReactElement) => {
        return itemSize;
      };

      const getHeight = () => {
        if (itemCount > 8) {
          return 8 * itemSize;
        }
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
      };

      const gridRef = useResetCache(itemCount);

      return (
        <div ref={ref}>
          <OuterElementContext.Provider value={other}>
            <VariableSizeList
              itemData={itemData}
              height={getHeight()}
              width="100%"
              ref={gridRef}
              outerElementType={OuterElementType}
              innerElementType="ul"
              itemSize={index => getChildSize(itemData[index])}
              overscanCount={5}
              itemCount={itemCount}
            >
              {renderRow}
            </VariableSizeList>
          </OuterElementContext.Provider>
        </div>
      );
    },
  );

  return useMemo(
    () => (
      <Controller
        name={name}
        control={form.control}
        render={({ field }) => {
          return (
            <ProgressOverlay isLoading={isLoading || !options?.[name]?.length} wrapperSx={{ width: "100%" }}>
              <Autocomplete
                open={open}
                onOpen={() => !readOnly && setOpen(true)}
                onClose={() => setOpen(false)}
                disableClearable={disableClear || readOnly}
                disableListWrap
                sx={{ my: 1 }}
                multiple={false}
                disabled={disabled}
                loading={isLoading || !options?.[name]?.length}
                options={options[name] || []}
                PopperComponent={StyledPopper}
                ListboxComponent={VirtualizedListBox}
                groupBy={option => (isGrouped ? (option.group as string) : "")}
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                renderGroup={params => params as any}
                popupIcon={<ExpandMore />}
                value={options[name]?.find((option: IAutocompleteOption) => value === option.id) || null}
                onChange={
                  onChange ||
                  ((_event: ChangeEvent<unknown>, option: IAutocompleteOption | null): void => {
                    const value = option ? option.id : 0;
                    form.setValue(name, value, { shouldDirty: true });
                  })
                }
                noOptionsText={formatMessage({ id: "form.hints.noOptions" })}
                loadingText={formatMessage({ id: "form.hints.loading" })}
                getOptionLabel={(option: IAutocompleteOption): string => option.title}
                renderOption={(props, option, state) => [props, option, state.index] as ReactNode}
                renderInput={(params: AutocompleteRenderInputParams): ReactElement => (
                  <TextField
                    {...field}
                    {...params}
                    inputProps={{ ...params.inputProps, readOnly }}
                    InputProps={{ ...params.InputProps }}
                    label={localizedLabel}
                    placeholder={localizedPlaceholder}
                    error={!!error}
                    helperText={localizedHelperText}
                    variant={variant}
                    onChange={() => {}}
                    fullWidth
                  />
                )}
              />
            </ProgressOverlay>
          );
        }}
      />
    ),
    [
      open,
      disableClear,
      readOnly,
      disabled,
      isLoading,
      name,
      options,
      options[name],
      form,
      localizedLabel,
      localizedPlaceholder,
      error,
      localizedHelperText,
      variant,
    ],
  );
};
